import { vec2, mat2d, glMatrix } from "gl-matrix";
import {
  rotateMatrixAroundPoint,
  limitAngle,
  constrainRadiansToAngle,
  getAngleFromRadians,
  getRadiansFromAngle,
} from "../math/Angles";
import AppConstants from "../../constants/AppConstants";
import { ELEMENT_ORDER, ELEMENTS } from "@/constants/EntityConstants";

glMatrix.setMatrixArrayType(Array);

/**
 * @typedef { import("./types").Target } target
 * @typedef { import("./types").Bounds } bounds
 * @typedef { import("./types").tool } tool
 * @typedef { import("./types").action } action
 * @typedef { import("./types").transformerBounds } transformerBounds
 * @typedef { import("./types").vec2 } vec2
 * @typedef { import("./types").Element } element
 */

/**
 * Creates a target object from the given element.
 * @param {element} passedElement - The HTML element to create the target from.
 * @returns {target} - The target generated from the element.
 */

export const getTarget = ({ x, y, w, h, a }, offsets) => {
  const target = {
    x: offsets.x + x,
    y: offsets.y + y,
    w,
    h,
    a,
    bounds: getTargetBounds({ x, y, w, h, a }, offsets),
  };

  return target;
};

/**
 * Creates a target object from a line element with appropriate offsets/padding.
 * @param {element} passedElement - The HTML element to create the target from.
 * @returns {target} - The target generated from the element.
 */

export const getLineTarget = ({ x, y, w, h, a }, offsets) => {
  const target = {
    x: offsets.x + x,
    y: offsets.y + y - 10,
    w,
    h: h + 20,
    a,
    bounds: getTargetBounds({ x, y, w, h, a }, offsets),
  };

  return target;
};

/**
 * Rotates a single corner around a center point and returns the translated XY coordinates.
 * @param {vec2} corner - vec2 representation of a corner to be rotated
 * @param {vec2} center - vec3 representation of the center point to rotate corner around
 * @param {number} angle - angle to rotate the corner by
 * @returns {vec2} - The rotated corner
 */

export const getBoundingCornerCoordinates = (corner, center, angle) => {
  const cornerMatrix = mat2d.create();
  rotateMatrixAroundPoint(cornerMatrix, center[0], center[1], angle);
  mat2d.translate(cornerMatrix, cornerMatrix, vec2.fromValues(corner[0], corner[1]));
  return [cornerMatrix[4], cornerMatrix[5]];
};

/**
 * Simulates getBoundingClientRect by taking the four corners of the unrotated element, using height and width plus it's coordinates to find it's corners, then rotating each corner by the element's angle from the center. The highest, lowest, leftmost, and rightmost values represent each side of it's box model, and then are offset.
 * @param {element} element - the element to generate target bounds from
 * @param {{
 *  x: number,
 *  y: number
 * }} offsets - xy offsets to shift the bounds by
 * @returns {bounds} - The target's generated bounds
 */

export const getTargetBounds = ({ x, y, w, h, a }, offsets) => {
  const bounds = { bottom: 0, left: 0, right: 0, top: 0 };

  if (!a) {
    bounds.bottom = offsets.y + y + h;
    bounds.left = offsets.x + x;
    bounds.right = offsets.x + x + w;
    bounds.top = offsets.y + y;
  } else {
    const rads = -getRadiansFromAngle(a);
    const center = [x + w / 2, y + h / 2];
    const corners = {
      tl: getBoundingCornerCoordinates([x, y], center, rads),
      tr: getBoundingCornerCoordinates([x + w, y], center, rads),
      bl: getBoundingCornerCoordinates([x, y + h], center, rads),
      br: getBoundingCornerCoordinates([x + w, y + h], center, rads),
    };

    const cornersX = [corners.tl[0], corners.tr[0], corners.br[0], corners.bl[0]];
    const cornersY = [corners.tl[1], corners.tr[1], corners.br[1], corners.bl[1]];

    bounds.bottom = Math.round(Math.max(...cornersY)) + offsets.y;
    bounds.left = Math.round(Math.min(...cornersX)) + offsets.x;
    bounds.right = Math.round(Math.max(...cornersX)) + offsets.x;
    bounds.top = Math.round(Math.min(...cornersY)) + offsets.y;
  }

  return bounds;
};

/**
 * Loops though the selected elements and returns a Map of targets.
 * @param {element[]} selectedElems - The selected elements.
 * @param {typeof import("@/constants/DesignConstants.mjs").LAYOUTS[number]} layout - The name of the attribute to use as the target's id.
 * @param {{ x: number, y: number}} offsets - x/y values to apply to targets. Generally represents space between stage and design container and y of selected block.
 * @returns {Record<String, target>} - A Map of targets where the key is the id of the target.
 */

export const getTargets = (selectedElems, layout, offsets) =>
  selectedElems.reduce((elems, elem) => {
    const targets = elems;
    const elemId = elem.selected.id;
    const target =
      elem.type === AppConstants.ELEMENT_TYPE_LINE
        ? getLineTarget(elem[layout], offsets)
        : getTarget(elem[layout], offsets);
    target.type = elem.type;

    // collect contained targets with no offset
    target.contained = elem[ELEMENT_ORDER]?.length
      ? elem[ELEMENT_ORDER].reduce((result, id) => {
          const containedElement = elem.elements[id];
          const selectionId = `${elemId}.${ELEMENTS}.${id}`;
          const containedTarget =
            containedElement.type === AppConstants.ELEMENT_TYPE_LINE
              ? getLineTarget(containedElement[layout], { x: 0, y: 0 })
              : getTarget(containedElement[layout], { x: 0, y: 0 });

          containedTarget.type = containedElement.type;
          result[selectionId] = containedTarget;
          return result;
        }, {})
      : undefined;

    targets[elemId] = target;
    return targets;
  }, {});

/**
 * Adjust a target's bounds data to be relative to the tool, as opposed to the entire canvas.
 * @param {Map<String, targets>} targets - A map of selected targets to adjust.
 * @param {bounds} parentBounds - The bounds of the total group of elements.
 * @param {tool} tool - Data representing the tool.
 * @returns
 */

export const adjustTargetsToTool = (targets, parentBounds, tool) => {
  const adjustedTargets = Object.entries(targets).reduce((acc, [id, prevTarget]) => {
    const target = { ...prevTarget };
    target.x -= parentBounds.left + tool.x;
    target.y -= parentBounds.top + tool.y;
    target.bounds.top -= parentBounds.top + tool.y;
    target.bounds.left -= parentBounds.left + tool.x;
    target.bounds.right -= parentBounds.left + tool.x;
    target.bounds.bottom -= parentBounds.top + tool.y;
    acc[id] = target;
    return acc;
  }, {});
  return adjustedTargets;
};

/**
 * Rotates an individual target around the center of the tool.
 * @param {tool} tool - Data representing the tool.
 * @param {action} action - Data representing the current action.
 * @param {target} target - The target to rotate.
 * @param {target} targetStart - Data representing the starting state of the target.
 * @param {number} constrainAngle - The angle to constrain the rotation to if constrain is true.
 * @returns {target} - The rotated target.
 */

export const rotateGroupedTarget = (tool, action, target, targetStart, constrainAngle) => {
  const { toolStart, constrain } = action;
  const targetStartRotation = limitAngle(targetStart.a);
  const rads = tool.r;
  const constrainedRads = constrain ? constrainRadiansToAngle(rads, constrainAngle) : 0;
  const dragRotation = tool.a - toolStart.a;

  const rotatePointX = toolStart.w / 2 - targetStart.x - target.w / 2;
  const rotatePointY = toolStart.h / 2 - targetStart.y - target.h / 2;

  const angleSum = dragRotation + targetStartRotation;
  const angle = limitAngle(angleSum);

  const targetMatrix = mat2d.create();
  mat2d.translate(targetMatrix, targetMatrix, vec2.fromValues(targetStart.x, targetStart.y));
  rotateMatrixAroundPoint(
    targetMatrix,
    rotatePointX,
    rotatePointY,
    constrain ? constrainedRads : rads
  );

  // add constrained rotation offset for starting angles not divisible by constraint angle
  if (constrain && toolStart.a % constrainAngle !== 0 && dragRotation !== 0) {
    rotateMatrixAroundPoint(
      targetMatrix,
      rotatePointX,
      rotatePointY,
      getRadiansFromAngle(dragRotation - getAngleFromRadians(constrainedRads))
    );
  }

  return { ...target, x: Math.round(targetMatrix[4]), y: Math.round(targetMatrix[5]), a: angle };
};

/**
 * Scales outline targets in a rotated tool based on the current tool's scale vs toolStart.
 * @param {Map<string, target>} outlineTargets - A map of target outlines stored before the scale transform started.
 * @param {number} sizeDiff - The result of tool.w / toolStart.w to multiply targetOutline bounds by.
 * @returns {Map<string, target>} - Scaled outline targets.
 */
export const scaleRotatedOutlineTargets = (outlineTargets, sizeDiff) =>
  Object.fromEntries(
    Object.entries(outlineTargets).map(([id, target]) => {
      const {
        bounds: { bottom, right, top, left },
      } = target;
      return [
        id,
        {
          ...target,
          bounds: {
            left: left * sizeDiff,
            top: top * sizeDiff,
            bottom: bottom * sizeDiff,
            right: right * sizeDiff,
          },
        },
      ];
    })
  );

/**
 * Adds difference from canvasBounds and containerBounds to the targets. 
 * This is used to give the target the correct position when the distance between 
 * the target and the edge of the stage changes but shouldn't move in relation to the canvas. 
 * Typically happens when a window resize, panel toggle, or zoom change happens.
 * @param {{
 *  targets: Map<string, target>,
 *  bounds: transformerBounds
 * }} props - contains targets to be offset and the transformer bound objects
 * @returns {Map<string, target>} - Targets with offsets added.
 */
export const offsetTargetsFromBounds = ({ targets, bounds }) => {
  const { canvasBounds, containerBounds } = bounds;
  return Object.fromEntries(
    Object.entries(targets).map(([id, target]) => {
      return [
        id,
        {
          ...target,
          x: target.x + (canvasBounds.x - containerBounds.x),
          y: target.y + (canvasBounds.y - containerBounds.y),
        },
      ];
    })
  );
};

/**
 * Subtracts the bounds from each given target, resulting in the targets having bounds relative to that bounding object, as opposed to the entire window.
 * @param {{
 *  targets: Map<string, target>,
 *  bounds: DOMRect
 * }} props - contains targets to be offset and the transformer bound objects
 * @returns {Map<string, target>} - Targets with new bounds data relative to the DOMRect bound object supplied.
 */
export const adjustTargetsToBounds = ({ targets, bounds }) => {
  const { x, y } = bounds;
  return Object.fromEntries(
    Object.entries(targets).map(([id, target]) => {
      return [
        id,
        {
          ...target,
          x: target.x - x,
          y: target.y - y,
        },
      ];
    })
  );
};

/**
 * Scales the target width, height, and bounds using the scaled W/H (ratios) and matrix.
 * Mutates the target
 * @param {Object} props
 * @param {target} props.target
 * @param {target} props.targetStart
 * @param {number} props.scaledW
 * @param {number} props.scaledH
 * @param {mat2d} props.scaleMatrix
 * @returns {target}
 */
export const scaleTarget = ({ target, targetStart, scaledW, scaledH, scaleMatrix }) => {
  const tW = scaledW * targetStart.w;
  const tH =
    target.type === AppConstants.ELEMENT_TYPE_LINE ? targetStart.h : scaledH * targetStart.h;

  const boundsTL = vec2.transformMat2(
    vec2.create(),
    vec2.fromValues(targetStart.bounds.left, targetStart.bounds.top),
    scaleMatrix
  );
  const boundsBR = vec2.transformMat2(
    vec2.create(),
    vec2.fromValues(targetStart.bounds.right, targetStart.bounds.bottom),
    scaleMatrix
  );

  [target.bounds.left, target.bounds.top] = boundsTL;
  [target.bounds.right, target.bounds.bottom] = boundsBR;

  target.w = tW;
  target.h = tH;

  return target;
};
